import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { exportAsistenciaExcel, exportAsistenciaPdf, exportExpedienteExcel, exportExpedientePdf, exportAsistenciaPdfGrafico, exportExpedientePdfGrafico, getAlumnos } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Download, FileXls, CalendarCheck, FileText, ChartBar, FilePdf
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const ReportesPage = () => {
  const { user, hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);

  const [asistenciaFilters, setAsistenciaFilters] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    grado: '',
    grupo: '',
    turno: '',
    matricula: '',
  });

  const handleExportAsistenciaConGrafico = async () => {
    if (!asistenciaFilters.fecha_inicio || !asistenciaFilters.fecha_fin) {
      toast.error('Selecciona el rango de fechas');
      return;
    }
    setLoading(true);
    try {
      // Si capturan matrícula, resolvemos alumno_id por búsqueda
      const params = { ...asistenciaFilters };
      delete params.matricula;
      if (asistenciaFilters.matricula) {
        try {
          const alumnoResp = await getAlumnos({ matricula: asistenciaFilters.matricula, limit: 1 });
          const found = (alumnoResp.data.alumnos || [])[0];
          if (!found) {
            toast.error('No se encontró alumno con esa matrícula');
            setLoading(false);
            return;
          }
          params.alumno_id = found.id;
        } catch (_) {
          toast.error('Error al buscar alumno');
          setLoading(false);
          return;
        }
      }
      const response = await exportAsistenciaPdfGrafico(params);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte_asistencia_grafico_${asistenciaFilters.fecha_inicio}_${asistenciaFilters.fecha_fin}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Reporte con gráfico descargado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  const [expedienteFilters, setExpedienteFilters] = useState({
    grado: '',
    grupo: '',
    turno: '',
    fecha_inicio: '',
    fecha_fin: '',
    matricula: '',
  });

  const handleExportExpedienteConGrafico = async () => {
    setLoading(true);
    try {
      const params = { ...expedienteFilters };
      // Remove empties
      Object.keys(params).forEach((k) => {
        if (!params[k] || params[k] === 'all') delete params[k];
      });
      const response = await exportExpedientePdfGrafico(params);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'reporte_expediente_grafico.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Reporte con gráficos descargado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  const handleExportAsistencia = async (format = 'excel') => {
    if (!asistenciaFilters.fecha_inicio || !asistenciaFilters.fecha_fin) {
      toast.error('Selecciona el rango de fechas');
      return;
    }
    
    setLoading(true);
    try {
      const response = format === 'pdf' 
        ? await exportAsistenciaPdf(asistenciaFilters)
        : await exportAsistenciaExcel(asistenciaFilters);
      
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `asistencia_${asistenciaFilters.fecha_inicio}_${asistenciaFilters.fecha_fin}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Reporte descargado');
    } catch (error) {
      toast.error('Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExpediente = async (format = 'excel') => {
    setLoading(true);
    try {
      const response = format === 'pdf'
        ? await exportExpedientePdf(expedienteFilters)
        : await exportExpedienteExcel(expedienteFilters);
      
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expediente.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Reporte descargado');
    } catch (error) {
      toast.error('Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reportes-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold">Reportes</h1>
        <p className="text-muted-foreground">Generación de reportes en Excel y PDF</p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="asistencia">
        <TabsList>
          <TabsTrigger value="asistencia" className="flex items-center gap-2">
            <CalendarCheck size={16} />
            Asistencia
          </TabsTrigger>
          {hasPermission(['administrador', 'direccion', 'prefectura']) && (
            <TabsTrigger value="expediente" className="flex items-center gap-2">
              <FileText size={16} />
              Expediente
            </TabsTrigger>
          )}
        </TabsList>

        {/* Asistencia Report */}
        <TabsContent value="asistencia" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarCheck size={20} />
                Reporte de Asistencia
              </CardTitle>
              <CardDescription>
                Exporta el registro de asistencia filtrado por fechas y grupo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div>
                  <Label>Fecha Inicio *</Label>
                  <Input
                    type="date"
                    value={asistenciaFilters.fecha_inicio}
                    onChange={(e) => setAsistenciaFilters(p => ({ ...p, fecha_inicio: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Fecha Fin *</Label>
                  <Input
                    type="date"
                    value={asistenciaFilters.fecha_fin}
                    onChange={(e) => setAsistenciaFilters(p => ({ ...p, fecha_fin: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Grado</Label>
                  <Select 
                    value={asistenciaFilters.grado} 
                    onValueChange={(v) => setAsistenciaFilters(p => ({ ...p, grado: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="1">1°</SelectItem>
                      <SelectItem value="2">2°</SelectItem>
                      <SelectItem value="3">3°</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Grupo</Label>
                  <Select 
                    value={asistenciaFilters.grupo} 
                    onValueChange={(v) => setAsistenciaFilters(p => ({ ...p, grupo: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="A">A</SelectItem>
                      <SelectItem value="B">B</SelectItem>
                      <SelectItem value="C">C</SelectItem>
                      <SelectItem value="D">D</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Turno</Label>
                  <Select 
                    value={asistenciaFilters.turno} 
                    onValueChange={(v) => setAsistenciaFilters(p => ({ ...p, turno: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="matutino">Matutino</SelectItem>
                      <SelectItem value="vespertino">Vespertino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={() => handleExportAsistencia('excel')} 
                    disabled={loading}
                    className="w-full"
                    data-testid="export-asistencia"
                  >
                    <FileXls size={16} className="mr-2" />
                    {loading ? 'Generando...' : 'Exportar Excel'}
                  </Button>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={() => handleExportAsistencia('pdf')} 
                    disabled={loading}
                    variant="outline"
                    className="w-full"
                    data-testid="export-asistencia-pdf"
                  >
                    <FilePdf size={16} className="mr-2" />
                    {loading ? 'Generando...' : 'Exportar PDF'}
                  </Button>
                </div>
              </div>

              {/* Sección: PDF con gráfico mensual */}
              <div className="mt-6 pt-6 border-t border-dashed">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <ChartBar size={18} className="text-primary" />
                  Reporte con gráfico mensual (PDF)
                </h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Genera un PDF con tabla resumen y gráfico de barras apiladas (asistencia / retardo / falta / justificado) agrupado por mes según el rango seleccionado. Opcionalmente filtra por alumno mediante su matrícula.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <Label>Matrícula del alumno (opcional)</Label>
                    <Input
                      value={asistenciaFilters.matricula}
                      onChange={(e) => setAsistenciaFilters(p => ({ ...p, matricula: e.target.value.toUpperCase() }))}
                      placeholder="Ej. 1MAT240101001"
                      data-testid="filter-matricula"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleExportAsistenciaConGrafico}
                      disabled={loading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="export-asistencia-pdf-grafico"
                    >
                      <ChartBar size={16} className="mr-2" />
                      {loading ? 'Generando...' : 'PDF con gráfico'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expediente Report */}
        {hasPermission(['administrador', 'direccion', 'prefectura']) && (
          <TabsContent value="expediente" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText size={20} />
                  Reporte de Expediente
                </CardTitle>
                <CardDescription>
                  Exporta el registro de incidencias filtrado por grupo
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Grado</Label>
                    <Select 
                      value={expedienteFilters.grado} 
                      onValueChange={(v) => setExpedienteFilters(p => ({ ...p, grado: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="1">1°</SelectItem>
                        <SelectItem value="2">2°</SelectItem>
                        <SelectItem value="3">3°</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Grupo</Label>
                    <Select 
                      value={expedienteFilters.grupo} 
                      onValueChange={(v) => setExpedienteFilters(p => ({ ...p, grupo: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="B">B</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                        <SelectItem value="D">D</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Turno</Label>
                    <Select 
                      value={expedienteFilters.turno} 
                      onValueChange={(v) => setExpedienteFilters(p => ({ ...p, turno: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="matutino">Matutino</SelectItem>
                        <SelectItem value="vespertino">Vespertino</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={() => handleExportExpediente('excel')} 
                      disabled={loading}
                      className="w-full"
                      data-testid="export-expediente"
                    >
                      <FileXls size={16} className="mr-2" />
                      {loading ? 'Generando...' : 'Exportar Excel'}
                    </Button>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={() => handleExportExpediente('pdf')} 
                      disabled={loading}
                      variant="outline"
                      className="w-full"
                      data-testid="export-expediente-pdf"
                    >
                      <FilePdf size={16} className="mr-2" />
                      {loading ? 'Generando...' : 'Exportar PDF'}
                    </Button>
                  </div>
                </div>

                {/* Sección: PDF con gráfico y filtro por alumno */}
                <div className="mt-6 pt-6 border-t border-dashed">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <ChartBar size={18} className="text-primary" />
                    Reporte con gráficos y filtro por alumno (PDF)
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Genera un PDF con tabla resumen por tipo de incidencia, gráfica de dona, barras horizontales y el detalle completo de incidencias. Opcionalmente, filtra por matrícula del alumno y por rango de fechas.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                      <Label>Fecha inicio</Label>
                      <Input
                        type="date"
                        value={expedienteFilters.fecha_inicio || ''}
                        onChange={(e) => setExpedienteFilters(p => ({ ...p, fecha_inicio: e.target.value }))}
                        data-testid="exp-graf-inicio"
                      />
                    </div>
                    <div>
                      <Label>Fecha fin</Label>
                      <Input
                        type="date"
                        value={expedienteFilters.fecha_fin || ''}
                        onChange={(e) => setExpedienteFilters(p => ({ ...p, fecha_fin: e.target.value }))}
                        data-testid="exp-graf-fin"
                      />
                    </div>
                    <div>
                      <Label>Matrícula (opcional)</Label>
                      <Input
                        value={expedienteFilters.matricula || ''}
                        onChange={(e) => setExpedienteFilters(p => ({ ...p, matricula: e.target.value.toUpperCase() }))}
                        placeholder="Ej. 2MAT240101001"
                        data-testid="exp-graf-matricula"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        onClick={handleExportExpedienteConGrafico}
                        disabled={loading}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid="export-expediente-pdf-grafico"
                      >
                        <ChartBar size={16} className="mr-2" />
                        {loading ? 'Generando...' : 'PDF con gráficos'}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default ReportesPage;
